import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Article } from '@/types/content';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';

const allArticles = articles as Article[];

const CategoryPage = () => {
  const { slug } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const [sort, setSort] = useState<'heat' | 'latest'>('heat');

  const list = useMemo(() => {
    const base = allArticles.filter((item) => item.categorySlug === slug);
    return sort === 'heat' ? [...base].sort((a, b) => b.heat - a.heat) : [...base].reverse();
  }, [slug, sort]);

  usePageMeta({
    title: category ? `${category.name} - 爆瓜爆料最新快讯 - 爆瓜爆料` : '栏目不存在 - 爆瓜爆料',
    description: category
      ? `${category.desc}爆瓜爆料为你实时追踪${category.name}相关爆料与第一现场进展，快讯即时更新。`
      : '该栏目暂时不存在，返回爆瓜爆料首页查看最新娱乐快讯。',
    keywords: category ? `${category.name},爆瓜爆料,${category.name}爆料,娱乐快讯` : '爆瓜爆料,娱乐快讯',
    canonical: category ? `https://sibzb.cn/category/${category.slug}` : 'https://sibzb.cn/',
  });

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center mb-5">
            <i className="ri-compass-3-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-3 text-sm text-foreground-600">换个入口，继续吃今天的瓜。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: `${category.name} - 爆瓜爆料`,
              description: category.desc,
              url: `https://sibzb.cn/category/${category.slug}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sibzb.cn/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                  item: `https://sibzb.cn/category/${category.slug}`,
                },
              ],
            },
            {
              '@type': 'ItemList',
              name: `${category.name}爆料列表`,
              itemListElement: list.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `https://sibzb.cn/article/${item.id}`,
                name: item.title,
              })),
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="flex-1 px-4 md:px-6 py-6 md:py-8">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: category.name }]} />

        <header className="mt-5 rounded-lg bg-background-100 border border-background-200 p-5 md:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="w-14 h-14 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
            <i className={`${category.icon} text-2xl`} />
          </span>
          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">{category.name}</h1>
            <p className="mt-2 text-sm leading-6 text-foreground-600 max-w-2xl">{category.desc}</p>
          </div>
          <span className="rounded-full bg-secondary-100 text-secondary-800 px-3 py-1 text-xs whitespace-nowrap">
            共 {list.length} 条在追踪
          </span>
        </header>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap gap-2" aria-label="栏目切换">
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  item.slug === category.slug
                    ? 'bg-primary-600 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort('heat')}
              className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                sort === 'heat' ? 'bg-accent-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              按热度
            </button>
            <button
              type="button"
              onClick={() => setSort('latest')}
              className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                sort === 'latest' ? 'bg-accent-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              按时间
            </button>
          </div>
        </div>

        {list.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {list.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 py-16 text-center">
            <p className="text-sm text-foreground-500">该栏目暂时还没有更新，先去首页看看今日热榜吧。</p>
            <Link to="/" className="mt-4 inline-flex text-sm text-primary-700 hover:underline">
              返回首页
            </Link>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
};

export default CategoryPage;